<script>
  /**
   * A list of OAuth scopes rendered as what they actually let a client do.
   *
   * Raw scope strings ("mcp.manage") mean nothing to the person being asked to
   * approve them, so each one is shown as an Italian sentence. Unknown scopes
   * are still listed verbatim rather than dropped: silently hiding part of what
   * is being granted would defeat the point of a consent screen.
   */
  import { Check, Ban } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';

  /** @type {string[]} Scopes to render */
  export let scopes = [];
  /** @type {'granted'|'denied'} How to colour the list */
  export let tone = 'granted';

  /** Scope identifier to its i18n key. */
  const SCOPE_KEYS = {
    openid: 'oauth.consent.scopeOpenid',
    offline_access: 'oauth.consent.scopeOfflineAccess',
    offline: 'oauth.consent.scopeOffline',
    'mcp.read': 'oauth.consent.scopeMcpRead',
    'mcp.manage': 'oauth.consent.scopeMcpManage',
  };

  /**
   * Describe a scope in Italian, falling back to the raw identifier.
   * @param {string} scope
   * @param {Function} translate The `$_` translator
   * @returns {string}
   */
  function describe(scope, translate) {
    const key = SCOPE_KEYS[scope];
    return key ? translate(key) : translate('oauth.consent.scopeUnknown', { values: { scope } });
  }
</script>

<ul class="space-y-2">
  {#each scopes as scope (scope)}
    <li class="flex items-start gap-3">
      <span
        class="mt-0.5 flex-shrink-0 rounded-full p-1 {tone === 'denied'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
          : 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300'}"
      >
        {#if tone === 'denied'}
          <Ban size={14} />
        {:else}
          <Check size={14} />
        {/if}
      </span>
      <span class="min-w-0">
        <span
          class="block text-sm {tone === 'denied'
            ? 'text-amber-800 dark:text-amber-200'
            : 'text-gray-800 dark:text-gray-100'}"
        >
          {describe(scope, $_)}
        </span>
        <code class="block text-xs text-gray-400 dark:text-gray-500 font-mono break-all">{scope}</code>
      </span>
    </li>
  {/each}
</ul>
